import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
  HttpUrlEncodingCodec,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { User } from '../../shared/models/user';
import { UserExportData } from '../../shared/models/user-export-data.model';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ServiceBase } from './service.base';
import { environment } from 'environments/environment';
import {
  INTERCEPTForbidden,
  INTERCEPTInternalServerError,
  INTERCEPTNotFound,
  INTERCEPTServiceUnavailable,
} from 'app/core/interceptors/contexts';

// Interfaces para respostas da API
interface UserEmailResponse {
  id: string;
  name: string;
}

interface ApiResponse {
  [key: string]: string;
}

@Injectable()
export class UserService extends ServiceBase {
  public usuarioLogado: User | null;
  public dataSource = new BehaviorSubject<User[]>([]);
  currentData = this.dataSource.asObservable();

  constructor(public http: HttpClient) {
    super();

    this.usuarioLogado = this.obterUsuarioLogado();
  }

  private getOcpAPIMSubscriptionKey() {
    if (environment.environmentName !== 'LOCAL') {
      return {
        headers: new HttpHeaders({
          'Ocp-Apim-Subscription-Key': this.subscriptionKey,
        }),
      };
    } else {
      return {};
    }
  }

  changeData(data: User[] | User): void {
    if (!(data instanceof Array)) data = [data];
    this.dataSource.next(data);
  }

  getUsuario(): Observable<User | null> {
    this.usuarioLogado = this.obterUsuarioLogado();
    if (!this.usuarioLogado?.id) return of(null);
    return this.http
      .get(
        `${this.UrlServiceV1}user?id=${this.usuarioLogado.id}`,
        this.obterAuthHeader(),
      )
      .pipe(map(this.extractData<User>), catchError(this.serviceError));
  }

  getUserByEmail(email: string): Observable<User | null> {
    return this.http
      .get(`${this.UrlServiceV1}user?id=${email}`, this.obterAuthHeader())
      .pipe(map(this.extractData<User>), catchError(this.serviceError));
  }

  getUserEmail(): Observable<UserEmailResponse[] | null> {
    this.usuarioLogado = this.obterUsuarioLogado();
    const params = new HttpParams({
      encoder: new HttpUrlEncodingCodec(),
    }).set('getEmail', 'true');

    return this.http
      .get<UserEmailResponse[]>(this.UrlServiceV1 + 'user/', {
        ...this.obterAuthHeader(),
        params,
      })
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  getProfileImage(): Observable<Blob | null> {
    return this.http
      .get(`${this.UrlServiceV1}user/profile-image`, {
        ...this.obterAuthHeader(),
        responseType: 'blob',
        context: new HttpContext()
          .set(INTERCEPTNotFound, false)
          .set(INTERCEPTInternalServerError, false),
      })
      .pipe(
        map(this.extractData),
        catchError(() => of(null)),
      );
  }

  getUsuarios(): Observable<User[]> {
    return this.http
      .get<User[]>(this.UrlServiceV1 + 'user', this.obterAuthHeader())
      .pipe(catchError(this.serviceError));
  }

  registerUsuario(user: User): Observable<User | null> {
    return this.http
      .post<User>(
        `${this.UrlServiceV1}user?id=${user.id}`,
        this.obterAuthHeader(),
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  patchUser(
    user: Partial<User>,
    options?: (keyof User)[],
  ): Observable<ApiResponse | null> {
    return this.http
      .patch<ApiResponse>(this.UrlServiceV1 + 'user/', user, {
        params: { options },
        ...this.obterAuthHeader(),
      })
      .pipe(map(this.extractData<ApiResponse>), catchError(this.serviceError));
  }

  /**
   * Saves the language the user picked in the web app as their preferred
   * language, which the backend uses for content generated for them (e.g.
   * TaskData designators and reminder emails). It is a background save:
   * failures neither show errors nor leave the page, and the preference is
   * saved again on the next language change.
   */
  updatePreferredLanguage(lang: string): Observable<ApiResponse | null> {
    return this.http
      .patch<ApiResponse>(
        this.UrlServiceV1 + 'user/',
        { lang },
        {
          params: { options: ['lang'] },
          context: new HttpContext()
            .set(INTERCEPTInternalServerError, false)
            .set(INTERCEPTServiceUnavailable, false)
            .set(INTERCEPTNotFound, false)
            .set(INTERCEPTForbidden, false),
          ...this.obterAuthHeader(),
        },
      )
      .pipe(map(this.extractData<ApiResponse>), catchError(this.serviceError));
  }

  saveUsuarioFreeTier(user: Partial<User>): Observable<User | null> {
    return this.http
      .post<User>(
        this.UrlServiceOauthV1 + 'signup',
        user,
        this.getOcpAPIMSubscriptionKey(),
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  forgotPassword(email: string, lang: string): Observable<ApiResponse | null> {
    return this.http
      .post<ApiResponse>(
        this.UrlServiceOauthV1 + `forgot-password`,
        { email, lang },
        this.getOcpAPIMSubscriptionKey(),
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  redefinirSenha(
    password: string,
    hash: string,
  ): Observable<ApiResponse | null> {
    return this.http
      .post<ApiResponse>(
        this.UrlServiceOauthV1 + `recover-password`,
        { password, hash },
        this.getOcpAPIMSubscriptionKey(),
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  confirmarCadastroUsuarioFreeTier(
    hash: string,
  ): Observable<ApiResponse | null> {
    return this.http
      .patch<ApiResponse>(
        `${this.UrlServiceOauthV1}signup`,
        { hash },
        this.getOcpAPIMSubscriptionKey(),
      )
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  uploadProfileImage(file: File): Observable<unknown> {
    const options = this.obterAuthHeaderForFileUpload();
    return this.http.put(`${this.UrlServiceV1}user/profile-image`, file, {
      ...options,
      headers: options.headers.set('Content-Type', file.type),
      context: new HttpContext().set(INTERCEPTInternalServerError, false),
    });
  }

  fakeDeleteUser(lang: string): Observable<unknown> {
    const url = `${this.UrlServiceV1}user?lang=${encodeURIComponent(lang)}`;
    return this.http
      .delete(url, this.obterAuthHeader())
      .pipe(map(this.extractData), catchError(this.serviceError));
  }

  exportUserData(): Observable<UserExportData> {
    return this.http
      .get<UserExportData>(this.UrlServiceV1 + 'user/export', {
        ...this.obterAuthHeader(),
      })
      .pipe(catchError(this.serviceError));
  }

  exportUserDataFull(lang: string): Observable<Blob> {
    const url = `${this.UrlServiceV1}user/export/full?lang=${encodeURIComponent(lang)}`;
    return this.http
      .get(url, {
        ...this.obterAuthHeader(),
        responseType: 'blob',
      })
      .pipe(catchError((error) => this.serviceError(error)));
  }

  reactivateAccount(): Observable<{ reactivated: boolean }> {
    return this.http
      .post<{
        reactivated: boolean;
      }>(this.UrlServiceV1 + 'user/reactivate', null, this.obterAuthHeader())
      .pipe(catchError((error) => this.serviceError(error)));
  }
}
